import Table from 'common/table/table';
import { Icons, RowIconAction } from 'common/table/table-helper';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Div, FormItem, IconText } from 'ui';

// eslint-disable-next-line
import ShipToComponent from 'common/partners/components/ship-to-component';
import { withShadow } from 'ui/styles';
import { severities } from 'utils/app-configs';
import SubcontractFooter from './subcontract-footer';

const Wrapper = styled(Div)`
  ${withShadow};
  border-radius: 4px;
  background-color: #fff;
  padding: 5px;
  margin-bottom: 20px;
`;

const SubcontractList = ({
  data = [],
  removeSubContract,
  disabled,
  intl,
  vendors,
  isVendorsLoading,
  addSubContract,
  setFormDirty,
  shipTo,
}) => {
  const getColumns = () => [
    {
      id: 'actions',
      displayName: '',
      fieldName: '',
      width: '60px',
      renderFn: r => <Icons>
        <RowIconAction
          title={intl.formatMessage({ id: 'order.tooltip.deleteSubContracting' })}
          tooltipKey='delete'
          iconKey='trash-alt'
          action={() => removeSubContract(r)}
          disabled={r.isFrozen || disabled}
        />
      </Icons>,
    },
    {
      id: 'itemTypeName',
      displayName: intl.formatMessage({ id: 'order.subcontracts.TYPE' }),
      fieldName: 'itemTypeName',
    },
    {
      id: 'itemDescription',
      displayName: intl.formatMessage({ id: 'order.subcontracts.DESCRIPTION' }),
      fieldName: 'itemDescription',
      tooltip: row => row.itemDescription,
    },
    {
      id: 'vendorName',
      displayName: intl.formatMessage({ id: 'order.subcontracts.VENDOR' }),
      fieldName: 'vendorName',
      tooltip: row => row.vendorName,
      width: '310px',
    },
    {
      id: 'costCurrency',
      displayName: intl.formatMessage({ id: 'order.subcontracts.COST_CURRENCY' }),
      tooltip: ({ cost, costCurrency }) =>
        `${cost} ${(costCurrency && costCurrency.toUpperCase()) || ''}`,
      renderFn: ({ cost, costCurrency }) =>
        `${cost} ${(costCurrency && costCurrency.toUpperCase()) || ''}`,
    },
    {
      id: 'poNumber',
      displayName: intl.formatMessage({ id: 'order.subcontracts.PO_NUMBER' }),
      fieldName: 'poNumber',
      tooltip: row => row.poNumber,
    },
    {
      id: 'goodsReceiptIndicator',
      displayName: intl.formatMessage({ id: 'order.subcontracts.GR_INDICATOR' }),
      fieldName: 'goodsReceiptIndicator',
      renderFn: row =>
        row.goodsReceiptIndicator ?
          <IconText
            iconType='check-circle'
            translationKey='common.OK'
            severity={severities.OK}
          /> :
          <IconText
            iconType='exclamation-triangle'
            translationKey='common.NOT_OK'
            severity={severities.WARNING}
          />,
    },
    {
      id: 'vendorInvoiceIndicator',
      displayName: intl.formatMessage({ id: 'order.subcontracts.VI_INDICATOR' }),
      fieldName: 'vendorInvoiceIndicator',
      renderFn: row =>
        row.vendorInvoiceIndicator ?
          <IconText
            iconType='check-circle'
            translationKey='common.OK'
            severity={severities.OK}
          /> :
          <IconText
            iconType='exclamation-triangle'
            translationKey='common.NOT_OK'
            severity={severities.WARNING}
          />,
    },
  ];

  const renderFooter = () => {
    if (disabled) {
      return null;
    }
    return (
      <SubcontractFooter
        vendors={vendors}
        isVendorsLoading={isVendorsLoading}
        addSubContract={addSubContract}
        setFormDirty={setFormDirty}
      />
    );
  };

  return (
    <Wrapper>
      <FormItem label={intl.formatMessage({ id: 'common.labels.SHIP_TO' })}>
        <ShipToComponent shipTo={shipTo} />
      </FormItem>
      <Table
        fullWidth={true}
        labelInColumn={true}
        columns={getColumns()}
        data={data}
        renderCustomFooter={renderFooter}
        hasCustomHeader={true}
      />
    </Wrapper>
  );
};

SubcontractList.propTypes = {
  data: PropTypes.array,
  removeSubContract: PropTypes.func.isRequired,
  addSubContract: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  shipTo: PropTypes.object,
  vendors: PropTypes.array,
  isVendorsLoading: PropTypes.bool,
  intl: PropTypes.object,
  setFormDirty: PropTypes.func,
};

export default injectIntl(SubcontractList);
